import React, { useState, useContext, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { setCookie } from "nookies";
import jwt_decode from "jwt-decode";
import { 
  IconMail, 
  IconKey, 
  IconArrowRight, 
  IconArrowLeft, 
  IconShieldCheck,
  IconSend,
  IconDevices,
  IconCheck
} from "@tabler/icons-react";

import Logo from "../images/mercadoagentes_logo.png";
import { AppSettings } from "../App";
import { Auth } from "../services/LoginServices";
import UserContext from "../contexts/UserContext";
import { useWhitelabel } from "../contexts/WhitelabelContext";
import AlertResponses from "../jsx/components/Alert";
import Swal from "sweetalert2";
import { WhitelabelLogo } from "../jsx/components/Whitelabel";
import TurnstileWidget from "../components/TurnstileWidget";
import { useTurnstile } from "../hooks/useTurnstile";

const LoginContent = (props) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();
  const {
    setError: setErrorToken,
    clearErrors: clearTokenErrors,
    formState: { errors: errorsToken },
  } = useForm();

  const [numbers, setNumbers] = useState(["", "", "", "", "", ""]);
  const { setUser, setLogged } = useContext(UserContext);
  const { brandName, isWhitelabel } = useWhitelabel();
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [notify, setNotify] = useState({
    title: "",
    message: "",
    type: "",
    visible: false,
  });

  const btnContinue = useRef();
  const btnAccess = useRef();
  const userEmail = useRef();
  const storeName = useRef("UpSoft");

  const [values, setValues] = useState(Array(6).fill(""));
  const inputsRef = useRef([]);
  
  // Turnstile integration
  const { config: turnstileConfig, shouldShow: shouldShowTurnstile } = useTurnstile();
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileError, setTurnstileError] = useState(null);
  const [turnstileResetTrigger, setTurnstileResetTrigger] = useState(0);

  const clearErrorMessage = () => {
    setTimeout(() => {
      clearTokenErrors("token");
    }, 4000);
  };

  // Turnstile handlers
  const handleTurnstileSuccess = (token) => {
    setTurnstileToken(token);
    setTurnstileError(null);
  };

  const handleTurnstileError = (error) => {
    setTurnstileError(error);
    setTurnstileToken(null);
  };

  const handleTurnstileExpired = () => {
    setTurnstileToken(null);
    setTurnstileError("Token de segurança expirado. Tente novamente.");
  };

  const resetTurnstile = () => {
    setTurnstileResetTrigger(prev => prev + 1);
    setTurnstileToken(null);
    setTurnstileError(null);
  };

  const onSubmit = (obj) => {
    // Validate Turnstile token if enabled
    if (shouldShowTurnstile && turnstileConfig?.enabled && !turnstileToken) {
      Swal.fire({
        title: "Verificação de Segurança",
        text: "Por favor, complete a verificação de segurança antes de continuar.",
        icon: "warning",
        confirmButtonColor: "var(--primary)",
        confirmButtonText: "Entendi!"
      });
      return;
    }

    let data = {
      email: obj.email,
      store_name: storeName.current,
      admin: 1,
    };

    // Add Turnstile token if available
    if (turnstileToken) {
      data.turnstile_token = turnstileToken;
    }

    userEmail.current = obj.email;

    btnContinue.current.innerText = "Enviando token de acesso...";
    btnContinue.current.disabled = true;

    Auth(data, "login", null, null, null)
      .then((res) => {
        if (res.status === 400) {
         
           Swal.fire({
             title: "Verificação de Segurança",
             text: "Validação de segurança obrigatória. Tente novamente.",
             icon: "warning",
             confirmButtonColor: "var(--primary)",
             confirmButtonText: "Tentar Novamente",
           });
           resetTurnstile();
           console.log(res?.data);
          btnContinue.current.innerText = "Continuar";
          btnContinue.current.removeAttribute("disabled");
          return;
        }

        if (res.status === 401) {
          Swal.fire({
            title: "Oops!",
            html: "Parece que você ainda não tem uma conta, ou digitou seu email errado. Por favor, <b>caso tenha conta verifique seu e-mail.</b>",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "var(--primary)",
            confirmButtonText: "Entendi!",
            cancelButtonText: "Cancelar",
          });
          btnContinue.current.innerText = "Continuar";
          btnContinue.current.removeAttribute("disabled");
          return;
        }

        if (res.status === 403) {
          Swal.fire({
            title: "Conta Desativada",
            text: "Sua conta precisa ser ativada. Verifique seu email para ativar sua conta antes de prosseguir com o login.",
            icon: "info",
            confirmButtonColor: "var(--primary)",
            confirmButtonText: "Entendi!",
          });
          btnContinue.current.innerText = "Continuar";
          btnContinue.current.removeAttribute("disabled");
          return;
        }

        if (res.status === 500) {
          if (props.wizardInstance) {
            props.setData({ email: obj.email });
            props.wizardInstance.nextStep();
          }
        } else {
          setStep(2);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Função auxiliar para processar login bem-sucedido
  const processSuccessfulLogin = (accessToken, refreshToken) => {
    let token = jwt_decode(accessToken);

    setCookie(undefined, "token", accessToken, {
      maxAge: 60 * 60 * 24 * 14, // 14 dias (token renova via refresh antes de expirar)
      path: "/",
      sameSite: "Lax",
    });

    // Salvar refresh token para renovação automática
    if (refreshToken) {
      setCookie(undefined, "refresh_token", refreshToken, {
        maxAge: 60 * 60 * 24 * 14, // 14 dias
        path: "/",
        sameSite: "Lax",
      });
    }

    // Salvar apenas dados básicos não sensíveis
    localStorage.setItem("lastEmailLogged", token.email);

    setCookie(undefined, "MA_AID", token.account_id, {
      maxAge: 60 * 60 * 24 * 14, // 14 dias
      path: "/",
    });

    setUser(token);
    setLogged(true);
  };

  const onSubmitToken = (obj) => {
    let arr = [];
    document.querySelectorAll(".token-input-modern").forEach((input) => {
      if (input.value) arr.push(input.value);
    });

    if (arr.length < 6) {
      setErrorToken("token", {
        type: "invalid",
        message: "Por favor, insira o token corretamente!",
      });
      clearErrorMessage();
      return;
    }
    let access = arr.join("");

    let data = {
      email: userEmail.current,
      token: access,
      store_name: AppSettings.store
        ? AppSettings.store.name
        : storeName.current,
      tokenForAdmin: true,
    };

    Auth(data, "login_verify", null, null, null)
      .then((res) => {
        btnAccess.current.innerText = "Acessando...";
        btnAccess.current.disabled = true;

        // Cenário: Token inválido (HTTP 400)
        if (res.status === 400) {
          setErrorToken("token", {
            type: "invalid",
            message:
              "Token inválido! Tente reenviar o token para seu e-mail novamente.",
          });
          btnAccess.current.innerText = "Acessar";
          btnAccess.current.removeAttribute("disabled");
          clearErrorMessage();
          return;
        }

        // Cenário: Acesso Restrito (plano irregular)
        if (res.restricted_access === true) {
          const { reason, message, details } = res.restriction || {};

          // Fazer login (token é válido, mas com restrições)
          processSuccessfulLogin(res.access, res.refresh);

          const iconMap = {
            expired: "error",
            suspended: "warning",
            canceled: "error",
            no_plan: "info"
          };

          const titleMap = {
            expired: "Plano Expirado",
            suspended: "Plano Suspenso",
            canceled: "Plano Cancelado",
            no_plan: "Sem Plano Ativo"
          };

          Swal.fire({
            title: titleMap[reason] || "Atenção",
            html: `
              <p>${message || "Sua conta possui restrições de acesso."}</p>
              ${details?.plan_name ? `<p><strong>Plano:</strong> ${details.plan_name}</p>` : ''}
              <hr/>
              <p><small>Você terá acesso limitado até regularizar sua situação.</small></p>
            `,
            icon: iconMap[reason] || "warning",
            confirmButtonColor: "var(--primary)",
            confirmButtonText: "Ir para Pagamentos",
            showCancelButton: true,
            cancelButtonText: "Ver Dashboard",
            allowOutsideClick: false,
          }).then((result) => {
            if (result.isConfirmed) {
              window.location.href = "/my-plan";
            } else {
              window.location.href = "/";
            }
          });

          return;
        }

        // Cenário: Login com Aviso (pagamento pendente em carência)
        if (res.warning) {
          processSuccessfulLogin(res.access, res.refresh);

          Swal.fire({
            title: "Atenção!",
            html: `
              <p>${res.warning.message || "Você possui um pagamento pendente."}</p>
              ${res.warning.details?.payment_due_date
                ? `<p><strong>Vencimento:</strong> ${res.warning.details.payment_due_date}</p>`
                : ''}
            `,
            icon: "warning",
            confirmButtonColor: "var(--primary)",
            confirmButtonText: "Regularizar Agora",
            showCancelButton: true,
            cancelButtonText: "Continuar",
          }).then((result) => {
            if (result.isConfirmed) {
              window.location.href = "/my-plan";
            } else {
              window.location.href = "/";
            }
          });

          return;
        }

        // Cenário: Login Normal (sem restrições)
        processSuccessfulLogin(res.access, res.refresh);
        window.location.href = "/";
      })
      .catch((error) => {
        btnAccess.current.innerText = "Acessar";
        btnAccess.current.removeAttribute("disabled");
        console.log(error);
        Swal.fire(
          "Oops!",
          "Ocorreu um erro inesperado, por favor, tente novamente. Se o problema persistir, contacte nosso setor de suporte."
        );
      });
  };

  useEffect(() => {
    let email = localStorage.getItem("lastEmailLogged");
    if (email) {
      setValue("email", email);
    }
  }, []);

  const handleChange = (e, index) => {
    const { value } = e.target;
    const newValues = [...values];

    if (value.length === 1) {
      newValues[index] = value;
      setValues(newValues);
      if (index < 5) {
        inputsRef.current[index + 1].focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("Text").slice(0, 6).split("");
    const newValues = [...values];

    pasteData.forEach((char, i) => {
      newValues[i] = char;
    });

    setValues(newValues);
    if (pasteData.length > 0 && pasteData.length < 6) {
      inputsRef.current[pasteData.length].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      const newValues = [...values];
      if (values[index] === "") {
        if (index > 0) {
          newValues[index - 1] = "";
          inputsRef.current[index - 1].focus();
        }
      } else {
        newValues[index] = "";
      }
      setValues(newValues);
    }
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  const handleInput = (e) => {
    if (e.target.value.length > 1) {
      const pasteData = e.target.value.slice(0, 6).split("");
      const newValues = [...values];

      pasteData.forEach((char, i) => {
        newValues[i] = char;
      });

      setValues(newValues);
      if (pasteData.length > 0 && pasteData.length < 6) {
        inputsRef.current[pasteData.length].focus();
      }
    }
  };

  return (
    <>
      <div className="modern-login-container">
        {/* Background with gradient */}
        <div className="login-background">
          <div className="gradient-overlay"></div>
          <div className="floating-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
            <div className="shape shape-4"></div>
          </div>
        </div>

        {/* Main content */}
        <div className="login-content">
          <div className="container">
            <div className="row justify-content-center align-items-center min-vh-100">
              <div className="col-sm-11 col-md-8 col-lg-6">
                {/* Login Card */}
                <div className="login-card">
                  {/* Logo and Header */}
                  <div className="login-header">
                    <div className="pb-4">
                      <WhitelabelLogo
                        type="main"
                        className="logo-img"
                        fallback={
                          <img
                            className="logo-img"
                            src={Logo}
                            alt="mercado agentes"
                          />
                        }
                      />
                    </div>
                    <h1 className="login-title">
                      {step === 1
                        ? "Bem-vindo de volta!"
                        : "Verificação de Segurança"}
                    </h1>
                    <p className="login-subtitle">
                      {step === 1
                        ? "Entre com seu e-mail para acessar o painel administrativo"
                        : "Digite o código de 6 dígitos enviado para seu e-mail"}
                    </p>
                  </div>

                  {/* Step Indicator */}
                  <div className="step-indicator">
                    <div className="step-container">
                      <div
                        className={`step ${
                          step === 1 ? "active" : "completed"
                        }`}
                      >
                        <div className="step-number">
                          {step === 1 ? (
                            <IconMail size={18} />
                          ) : (
                            <IconCheck size={18} />
                          )}
                        </div>
                        <span className="step-label">E-mail</span>
                      </div>
                      <div className="step-line"></div>
                      <div
                        className={`step ${step === 2 ? "active" : "pending"}`}
                      >
                        <div className="step-number">
                          {step === 2 ? <IconShieldCheck size={18} /> : 2}
                        </div>
                        <span className="step-label">Código</span>
                      </div>
                    </div>
                  </div>

                  {/* Forms */}
                  <div className="login-forms">
                    {/* Email Step */}
                    {step === 1 && (
                      <div className="email-form animate-slide-in">
                        <form
                          onSubmit={handleSubmit(onSubmit)}
                          className="modern-form"
                        >
                          <div className="form-group">
                            <label className="form-label">
                              <IconMail size={18} />
                              Endereço de e-mail
                            </label>
                            <div className="input-wrapper">
                              <input
                                type="email"
                                placeholder="seu@email.com"
                                className={`form-control modern-input ${
                                  errors.email ? "error" : ""
                                }`}
                                {...register("email", {
                                  required: true,
                                  minLength: 3,
                                  pattern:
                                    /^[a-zA-Z0-9._-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/,
                                })}
                              />
                              <div className="input-focus-border"></div>
                            </div>
                            {errors.email && (
                              <div className="error-message">
                                <IconShieldCheck size={16} />
                                {errors.email.type === "required"
                                  ? "E-mail é obrigatório"
                                  : "Digite um e-mail válido"}
                              </div>
                            )}
                          </div>

                          {/* Turnstile Widget */}
                          {shouldShowTurnstile && (
                            <div className="form-group">
                              <TurnstileWidget
                                onTokenChange={handleTurnstileSuccess}
                                onError={handleTurnstileError}
                                onExpired={handleTurnstileExpired}
                                resetTrigger={turnstileResetTrigger}
                                className="login-turnstile"
                              />
                              {turnstileError && (
                                <div className="error-message">
                                  <IconShieldCheck size={16} />
                                  {turnstileError}
                                </div>
                              )}
                            </div>
                          )}

                          <button
                            type="submit"
                            className="btn-modern-primary"
                            ref={(e) => (btnContinue.current = e)}
                            disabled={
                              isLoading ||
                              (shouldShowTurnstile && !turnstileToken)
                            }
                          >
                            <span className="btn-text">
                              {isLoading
                                ? "Enviando código..."
                                : shouldShowTurnstile && !turnstileToken
                                ? "Complete a verificação de segurança"
                                : "Continuar"}
                            </span>
                            <IconArrowRight size={18} className="btn-icon" />
                          </button>
                        </form>
                      </div>
                    )}

                    {/* Token Step */}
                    {step === 2 && (
                      <div className="token-form animate-slide-in">
                        <div className="token-info">
                          <div className="sent-to-info">
                            <IconSend size={20} />
                            <div>
                              <p className="sent-label">Código enviado para:</p>
                              <p className="sent-email">{userEmail.current}</p>
                            </div>
                          </div>
                        </div>

                        <form
                          onSubmit={handleSubmit(onSubmitToken)}
                          className="modern-form"
                        >
                          <div className="form-group">
                            <label className="form-label">
                              <IconKey size={18} />
                              Código de verificação
                            </label>

                            <div
                              className="token-inputs-container"
                              onPaste={(e) => handlePaste(e)}
                            >
                              {values.map((value, index) => (
                                <input
                                  key={index}
                                  className="token-input-modern"
                                  type="text"
                                  maxLength="1"
                                  value={value}
                                  onInput={handleInput}
                                  onChange={(e) => handleChange(e, index)}
                                  onKeyDown={(e) => handleKeyDown(e, index)}
                                  onFocus={handleFocus}
                                  ref={(el) => (inputsRef.current[index] = el)}
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  placeholder="0"
                                />
                              ))}
                            </div>

                            {errorsToken.token && (
                              <div className="error-message">
                                <IconShieldCheck size={16} />
                                {errorsToken.token.message}
                              </div>
                            )}
                          </div>

                          <button
                            type="submit"
                            className="btn-modern-primary"
                            ref={(e) => (btnAccess.current = e)}
                            disabled={isLoading}
                          >
                            <span className="btn-text">
                              {isLoading ? "Verificando..." : "Acessar Painel"}
                            </span>
                            <IconArrowRight size={18} className="btn-icon" />
                          </button>

                          <button
                            type="button"
                            className="btn-modern-secondary"
                            onClick={() => setStep(1)}
                          >
                            <IconArrowLeft size={18} className="btn-icon" />
                            <span className="btn-text">Voltar</span>
                          </button>
                        </form>

                        <div className="resend-info">
                          <p>Não recebeu o código?</p>
                          <button
                            className="resend-btn"
                            onClick={() => {
                              if (userEmail.current) {
                                // Mostrar loading
                                const resendBtn =
                                  document.querySelector(".resend-btn");
                                const originalText = resendBtn.innerText;
                                resendBtn.innerText = "Reenviando...";
                                resendBtn.disabled = true;

                                // Reenviar código usando o email armazenado
                                const data = {
                                  email: userEmail.current,
                                  store_name: storeName.current,
                                  admin: 1,
                                };

                                Auth(data, "login", null, null, null)
                                  .then((res) => {
                                    if (
                                      res.status === 400 ||
                                      res.status === 401
                                    ) {
                                      Swal.fire({
                                        title: "Erro!",
                                        text: "Não foi possível reenviar o código. Verifique seu e-mail.",
                                        icon: "error",
                                        confirmButtonColor: "var(--primary)",
                                      });
                                    } else {
                                      Swal.fire({
                                        title: "Código Reenviado!",
                                        text: "Um novo código foi enviado para seu e-mail.",
                                        icon: "success",
                                        timer: 3000,
                                        showConfirmButton: false,
                                      });
                                    }
                                  })
                                  .catch((error) => {
                                    console.error(
                                      "Erro ao reenviar código:",
                                      error
                                    );
                                    Swal.fire({
                                      title: "Erro!",
                                      text: "Erro inesperado ao reenviar código. Tente novamente.",
                                      icon: "error",
                                      confirmButtonColor: "var(--primary)",
                                    });
                                  })
                                  .finally(() => {
                                    // Restaurar botão
                                    resendBtn.innerText = originalText;
                                    resendBtn.disabled = false;
                                  });
                              }
                            }}
                          >
                            Reenviar código
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Signup link */}
                  {!isWhitelabel && (
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                      <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary, #475569)' }}>
                        Ainda não tem uma conta?{' '}
                        <a
                          href="/signup"
                          style={{ color: 'var(--primary, #3b82f6)', fontWeight: 600, textDecoration: 'none' }}
                        >
                          Testar grátis
                        </a>
                      </span>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="login-footer">
                    <p className="copyright-text text-primary">
                      © {new Date().getFullYear()} Desenvolvido por {brandName}
                    </p>
                    {!isWhitelabel && (
                      <p style={{ fontSize: '0.8rem', marginTop: '8px', marginBottom: 0 }}>
                        <Link
                          to="/politica-de-privacidade"
                          style={{ color: 'var(--text-secondary, #64748b)', textDecoration: 'none' }}
                        >
                          Política de Privacidade
                        </Link>
                        <span style={{ color: 'var(--text-secondary, #64748b)', margin: '0 8px' }}>·</span>
                        <Link
                          to="/termos-de-uso"
                          style={{ color: 'var(--text-secondary, #64748b)', textDecoration: 'none' }}
                        >
                          Termos de Uso
                        </Link>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications */}
        {notify.visible && (
          <AlertResponses
            title={notify.title}
            message={notify.message}
            type={notify.type}
            visible={notify.visible}
            setNotify={setNotify}
            pageLogin={true}
          />
        )}
      </div>
    </>
  );
};

// Wrapper principal do Login
const Login = (props) => {
  return <LoginContent {...props} />;
};

export default Login;
